import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigationType } from "react-router-dom";
import { useProjects } from "../data/projects";
import { useWorkPage, useFeaturedSlugs, useFilterGroups } from "../data/content";
import Emphasis from "../components/Emphasis";
import { labelToSlug, slugToLabel, groupCats, projectCats } from "../data/workGroups";
import ProjectCard from "./PagesComps/ProjectCard";
import WorkSearch from "./PagesComps/WorkSearch";
import { searchProjects } from "./PagesComps/search";
import { smoothScrollTo } from "../components/smoothScroll";

const ALL = "All";

// Chips are content now, so everything that used to read a module-level
// constant takes the group list instead.
const matches = (project, label, groups) =>
  projectCats(project).some((c) => groupCats(label, groups).includes(c));

const countFor = (cat, list, groups) =>
  cat === ALL ? list.length : list.filter((p) => matches(p, cat, groups)).length;

// The search trigger has to read as another chip, so both use this.
const PILL =
  "flex-shrink-0 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full font-sans text-xs sm:text-sm transition-colors border";
const PILL_IDLE =
  "border-border-light dark:border-border-dark text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:border-text-primary-light dark:hover:border-white";
const PILL_ACTIVE =
  "bg-text-primary-light dark:bg-white text-white dark:text-black border-text-primary-light dark:border-white";

// How long the row travels while the search opens or closes. Keep it level with
// the longest `duration-*` on the search below — the width is animated by CSS,
// the scroll by JavaScript, and if the two disagree one finishes first and the
// push stutters at whichever end is shorter.
const SEARCH_TRAVEL_MS = 700;

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

function AllWork() {
  const projects = useProjects();
  const page = useWorkPage();
  const featuredSlugs = useFeaturedSlugs();
  const groups = useFilterGroups();

  const [searchParams, setSearchParams]     = useSearchParams();
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasInteracted, setHasInteracted]   = useState(false);
  const scrollRef    = useRef(null);
  const filterBarRef = useRef(null);
  const gridRef      = useRef(null);
  const navType = useNavigationType();
  const firstRun = useRef(true);

  // Search takes over the whole filter row, so the chips are not merely
  // inactive while it is open — they are gone. The chip in play is remembered
  // so closing search puts the row back the way it was.
  const query = searchParams.get("q") ?? "";
  const [searchOpen, setSearchOpen] = useState(() => searchParams.has("q"));
  const [chipBeforeSearch, setChipBeforeSearch] = useState(null);

  const paramSlug    = searchParams.get("c");
  const activeFilter = paramSlug ? (slugToLabel(paramSlug, groups) ?? ALL) : ALL;

  // Counted once per chip rather than once per render of each button. A chip
  // with nothing behind it is a dead button, so it is dropped — except the one
  // currently selected, which stays visible so a ?c= link still reads clearly.
  const chips = [ALL, ...groups.map((g) => g.label)]
    .map((label) => ({ label, count: countFor(label, projects, groups) }))
    .filter(({ label, count }) => count > 0 || label === activeFilter);

  // Anything that changes which projects are shown has to reconcile the scroll
  // position, or the results land somewhere you are not looking. Search from
  // far down the page and a handful of matches sit above the viewport entirely;
  // close it again and the grid shrinks out from under you. Only pulls up,
  // never down, so it cannot yank the page while you are reading.
  const revealGrid = () => {
    if (!gridRef.current || !filterBarRef.current) return;

    const gridAbsTop = gridRef.current.getBoundingClientRect().top + window.scrollY;
    const filterBarBottom = filterBarRef.current.getBoundingClientRect().bottom;
    const targetScrollY = gridAbsTop - filterBarBottom;

    if (window.scrollY > targetScrollY) smoothScrollTo(targetScrollY);
  };

  const setQuery = (value) => {
    setSearchParams(value ? { q: value } : {}, { replace: true });
    revealGrid();
  };

  const openSearch = () => {
    setChipBeforeSearch(searchParams.get("c"));
    setSearchParams({}, { replace: true });
    setSearchOpen(true);
    revealGrid();
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchParams(chipBeforeSearch ? { c: chipBeforeSearch } : {}, { replace: true });
    revealGrid();
  };

  const setActiveFilter = (cat) => {
    if (cat === activeFilter) return;
    revealGrid();
    if (cat === ALL) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ c: labelToSlug(cat) }, { replace: true });
    }
  };

  useEffect(() => {
    if (page.documentTitle) document.title = page.documentTitle;
  }, [page.documentTitle]);

  useEffect(() => {
    const savedY = sessionStorage.getItem("workScrollY");
    if (savedY === null) return;
    if (navType !== "POP") {
      sessionStorage.removeItem("workScrollY");
      return;
    }
    const y = parseInt(savedY, 10);
    window.scrollTo({ top: 0, behavior: "instant" });
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        sessionStorage.removeItem("workScrollY");
        smoothScrollTo(y);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateButtons();
    el.addEventListener("scroll", updateButtons);
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, []);

  // The chips are never hidden — they are pushed out of frame by the search
  // growing beside them, which only reads as a push if the row travels with the
  // width as it animates.
  //
  // scrollLeft is pinned to the maximum rather than eased toward it. Easing
  // makes the scroll lag the growing width, which runs the search's right edge
  // — and the X on it — past the right of the frame until the scroll catches
  // up. Pinned, the search stays flush right the whole way. The jump that
  // pinning would otherwise cause is taken before the width starts moving,
  // while the chip is still 48px wide and a scroll is barely visible.
  //
  // Also re-measures the arrows at the end: neither scroll nor resize fires
  // when the row's contents change width, so they would otherwise keep whatever
  // state they held before.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    // Effects run on mount too, and there is nothing to animate then. Left to
    // run, this drags the row to its end the moment /work opens — you arrive on
    // the last chips instead of All, and cannot scroll back while it holds.
    // A page arriving with ?q= already open does belong at the end, but it
    // belongs there immediately rather than travelling.
    if (firstRun.current) {
      firstRun.current = false;
      if (searchOpen) el.scrollLeft = el.scrollWidth;
      updateButtons();
      return undefined;
    }

    // Settle to the end first, before the width starts moving. The search is
    // still a small chip at this point, so the scroll is almost invisible — and
    // in practice the row is already near the end anyway, since reaching the
    // search chip to click it means having scrolled there.
    el.scrollLeft = el.scrollWidth;

    const started = performance.now();

    let raf = requestAnimationFrame(function pin(now) {
      el.scrollLeft = el.scrollWidth; // clamps to the maximum on its own

      if (now - started < SEARCH_TRAVEL_MS) raf = requestAnimationFrame(pin);
      else updateButtons();
    });

    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOpen]);

  const scroll = (dir) => {
    setHasInteracted(true);
    scrollRef.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  const handleScrollInteraction = () => {
    if (!hasInteracted) setHasInteracted(true);
  };

  // Featured projects lead the grid, in the order set on the home page.
  const rank = (p) => {
    const i = featuredSlugs.indexOf(p.slug);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  const filtered = searchOpen
    ? searchProjects(projects, query)
    : projects
        .filter((p) => activeFilter === ALL || matches(p, activeFilter, groups))
        .sort((a, b) => rank(a) - rank(b));

  const btnClass = "flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border border-border-light dark:border-border-dark bg-white dark:bg-black text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors";

  return (
    <main className="bg-background-light dark:bg-background-dark">

      {/* Fixed overlay — masks content scrolling into the navbar gap above sticky tabs */}
      <div className="fixed top-0 left-0 right-0 h-16 md:h-20 z-40 bg-background-light dark:bg-background-dark pointer-events-none" />

      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 pt-32">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-px bg-text-muted-light dark:bg-text-muted-dark" />
            <span className="font-sans text-xs tracking-[0.3em] uppercase text-text-muted-light dark:text-text-muted-dark">
              {page.eyebrow}
            </span>
            <div className="w-8 h-px bg-text-muted-light dark:bg-text-muted-dark" />
          </div>

          <h1 className="font-sans font-bold text-6xl md:text-8xl text-text-primary-light dark:text-text-primary-dark leading-tight mb-6">
            <Emphasis>{page.heading}</Emphasis>
          </h1>

          <p className="font-sans text-base text-text-muted-light dark:text-text-muted-dark max-w-md mx-auto leading-relaxed">
            {page.blurb}
          </p>
        </div>
      </div>

      {/* Filters — sticky below navbar */}
      <div ref={filterBarRef} className="sticky top-16 md:top-20 z-40 bg-background-light dark:bg-background-dark mb-10">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <div className="relative">

            <div>
              {/* Left edge — gradient container + button */}
              <div className={`absolute left-0 top-0 bottom-0 z-10 flex items-center pr-4 bg-gradient-to-r from-white dark:from-black to-transparent transition-opacity duration-200 ${canScrollLeft && !searchOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <button onClick={() => scroll(-1)} aria-label="Scroll filters left" className={btnClass}>
                  <span className={!hasInteracted ? "animate-nudge-left" : ""}><ChevronLeft /></span>
                </button>
              </div>

              {/* Right edge — gradient container + button */}
              <div className={`absolute right-0 top-0 bottom-0 z-10 flex items-center pl-4 bg-gradient-to-l from-white dark:from-black to-transparent transition-opacity duration-200 ${canScrollRight && !searchOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <button onClick={() => scroll(1)} aria-label="Scroll filters right" className={btnClass}>
                  <span className={!hasInteracted ? "animate-nudge-right" : ""}><ChevronRight /></span>
                </button>
              </div>

              {/* Scrollable tabs */}
              <div
                ref={scrollRef}
                onScroll={handleScrollInteraction}
                className="flex items-center gap-2 overflow-x-auto py-1"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {/* Keeps its width throughout. The search growing beside it is
                    what pushes it off the left edge — it is not hidden, it is
                    shoved out of frame, and closing brings it back. */}
                <div
                  className={`flex items-center gap-2 flex-none ${searchOpen ? "pointer-events-none" : ""}`}
                  aria-hidden={searchOpen}
                >
                  {chips.map(({ label: cat, count }) => (
                    <button
                      key={cat}
                      onClick={() => setActiveFilter(cat)}
                      tabIndex={searchOpen ? -1 : 0}
                      className={`${PILL} ${activeFilter === cat ? PILL_ACTIVE : PILL_IDLE}`}
                    >
                      {cat}
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full font-sans text-xs leading-none ${
                        activeFilter === cat
                          ? "bg-white/20 dark:bg-black/20"
                          : "bg-border-light dark:bg-border-dark text-text-muted-light dark:text-text-muted-dark"
                      }`}>
                        {count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* The last chip in the row, always at its right edge. Closed
                    it is a square holding the icon; open it is the whole row.
                    The widths below are the only thing that animates — what is
                    inside never changes, it is just clipped.

                    Closed width equals chip height, which is what makes it
                    square: 26px on mobile, 34px from sm.

                    Keep this duration and SEARCH_TRAVEL_MS at the same number:
                    the width is animated here by CSS, the scroll that carries
                    the chips is animated in JavaScript, and they have to finish
                    together. */}
                <div
                  className={`flex-none overflow-hidden rounded-full transition-all ease-out ${
                    searchOpen
                      ? "duration-700 w-full"
                      : "duration-700 w-[26px] sm:w-[34px]"
                  }`}
                >
                  <WorkSearch
                    open={searchOpen}
                    value={query}
                    onChange={setQuery}
                    onOpen={openSearch}
                    onClose={closeSearch}
                    count={filtered.length}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Grid */}
      <div ref={gridRef} className="max-w-5xl mx-auto px-6 pb-24">
        {filtered.length === 0 ? (
          <p className="font-sans text-sm text-text-muted-light dark:text-text-muted-dark text-center py-16">
            0 Results
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
            {filtered.map((project) => (
              <ProjectCard key={project.slug} {...project} />
            ))}
          </div>
        )}
      </div>

    </main>
  );
}

export default AllWork;
